import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─── Category config ──────────────────────────────────────────── */
const CATS = {
  'Food & Drink':       { emoji: '🍔', color: '#C8A97E', glow: 'rgba(200,169,126,0.55)' },
  'Nightlife':          { emoji: '🌙', color: '#6B7CFF', glow: 'rgba(107,124,255,0.55)' },
  'Fitness & Wellness': { emoji: '💪', color: '#5DBB63', glow: 'rgba(93,187,99,0.55)'   },
  'Activities':         { emoji: '🎯', color: '#C07CFF', glow: 'rgba(192,124,255,0.55)' },
  'Free Deals':         { emoji: '🎁', color: '#FF7C7C', glow: 'rgba(255,124,124,0.55)' },
  'Convenience Stores': { emoji: '🛒', color: '#5B9EA0', glow: 'rgba(91,158,160,0.55)'  },
  'Liquor Stores':      { emoji: '🍷', color: '#8B4A62', glow: 'rgba(139,74,98,0.55)'   },
};
const FILTERS = ['All', ...Object.keys(CATS)];

const MAP_STYLES = [
  { id: 'warm',    label: '☀️ Warm',  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png' },
  { id: 'minimal', label: '◻ Clean', url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'           },
  { id: 'night',   label: '🌙 Night', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'            },
];

/* ─── Distance helpers ─────────────────────────────────────────── */
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDist(miles) {
  if (miles < 0.1) return { label: `${Math.round(miles * 5280)} ft`, walk: '<1 min walk', drive: '<1 min drive' };
  const walkMins = Math.max(1, Math.round(miles / 3 * 60));
  const driveMins = Math.max(1, Math.round(miles / 20 * 60));
  return {
    label: `${miles.toFixed(1)} mi`,
    walk: walkMins < 60 ? `${walkMins}m walk` : `${Math.ceil(walkMins / 60)}h walk`,
    drive: driveMins < 60 ? `${driveMins}m drive` : `${Math.ceil(driveMins / 60)}h drive`,
  };
}

/* ─── CSS ──────────────────────────────────────────────────────── */
const MARKER_CSS = `
@keyframes pk-pop    { 0%{transform:scale(0) translateY(8px);opacity:0} 75%{transform:scale(1.12) translateY(-2px)} 100%{transform:scale(1);opacity:1} }
@keyframes pk-ring   { 0%{transform:scale(1);opacity:.65} 100%{transform:scale(2.8);opacity:0} }
@keyframes pk-float  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes pk-glow   { 0%,100%{opacity:.45} 50%{opacity:.85} }

.pk-pin   { position:relative;display:flex;align-items:center;justify-content:center;cursor:pointer;animation:pk-pop .38s cubic-bezier(.34,1.56,.64,1) forwards; }
.pk-std   { width:40px;height:40px;border-radius:50%;border:3px solid rgba(255,255,255,.92);box-shadow:0 4px 18px rgba(0,0,0,.22);display:flex;align-items:center;justify-content:center;font-size:18px;z-index:2;position:relative;transition:transform .2s; }
.pk-std:hover { transform:scale(1.15); }
.pk-promo { width:54px;height:54px;border-radius:50%;border:3.5px solid rgba(255,255,255,.95);box-shadow:0 6px 26px rgba(0,0,0,.28);display:flex;align-items:center;justify-content:center;font-size:24px;z-index:2;position:relative;overflow:hidden;animation:pk-float 3s ease-in-out 2s infinite; }
.pk-promo:hover { transform:scale(1.12); }
.pk-ring  { position:absolute;border-radius:50%;border:2px solid;animation:pk-ring 2.2s ease-out infinite;z-index:1; }
.pk-ring2 { animation-delay:.9s; }
.pk-glow  { position:absolute;border-radius:50%;animation:pk-glow 2s ease-in-out infinite;z-index:0; }
.pk-badge { position:absolute;top:-5px;right:-5px;width:18px;height:18px;background:#C8A97E;border:2.5px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:900;color:white;z-index:3;letter-spacing:0; }

.leaflet-popup-content-wrapper{border-radius:18px!important;padding:0!important;overflow:hidden;box-shadow:0 14px 44px rgba(43,43,43,.18)!important;border:1px solid rgba(138,154,139,.14)!important;}
.leaflet-popup-content{margin:0!important;}
.leaflet-popup-tip-container,.leaflet-popup-close-button{display:none!important;}
.pk-popup{font-family:Montserrat,sans-serif;min-width:240px;max-width:290px;position:relative;}
.pk-popup-img{width:100%;height:115px;object-fit:cover;display:block;}
.pk-popup-promo-label{position:absolute;top:8px;right:8px;background:#C8A97E;color:white;font-size:9px;font-weight:800;padding:3px 8px;border-radius:20px;letter-spacing:.05em;text-transform:uppercase;}
.pk-popup-body{padding:12px 14px 14px;}
`;

/* Escape user-controlled values before injecting into Leaflet marker/popup HTML strings.
   Leaflet's divIcon/popup require raw HTML strings, so React's built-in XSS protection
   doesn't apply here — we escape business-supplied fields manually. */
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, ch =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

/* ─── Icon builders ────────────────────────────────────────────── */
function buildStdIcon(deal, delay = 0) {
  const c = CATS[deal.category] || CATS['Food & Drink'];
  const html = `
    <div class="pk-pin" style="animation-delay:${delay}ms">
      <div class="pk-ring" style="width:58px;height:58px;top:-9px;left:-9px;border-color:${c.color};opacity:.45"></div>
      <div class="pk-ring pk-ring2" style="width:58px;height:58px;top:-9px;left:-9px;border-color:${c.color};opacity:.28"></div>
      <div class="pk-std" style="background:${c.color};animation:pk-float ${2.8 + delay * 0.0008}s ease-in-out ${delay + 2200}ms infinite">${c.emoji}</div>
    </div>`;
  if (!window.L) return null;
  return window.L.divIcon({ html, className: '', iconSize: [40, 40], iconAnchor: [20, 20], popupAnchor: [0, -25] });
}

function buildPromoIcon(deal, delay = 0) {
  const c = CATS[deal.category] || CATS['Food & Drink'];
  const inner = deal.logo_url
    ? `<img src="${esc(deal.logo_url)}" style="width:48px;height:48px;object-fit:cover;border-radius:50%" onerror="this.outerHTML='<span style=\\'font-size:24px\\'>${c.emoji}</span>'" />`
    : `<span style="font-size:24px">${c.emoji}</span>`;
  const html = `
    <div class="pk-pin" style="animation-delay:${delay}ms">
      <div class="pk-glow" style="width:72px;height:72px;top:-9px;left:-9px;background:${c.glow};filter:blur(8px)"></div>
      <div class="pk-ring" style="width:78px;height:78px;top:-12px;left:-12px;border-color:${c.color};opacity:.5"></div>
      <div class="pk-ring pk-ring2" style="width:78px;height:78px;top:-12px;left:-12px;border-color:${c.color};opacity:.3"></div>
      <div class="pk-promo" style="background:${c.color};box-shadow:0 0 22px ${c.glow},0 6px 22px rgba(0,0,0,.26)">${inner}</div>
      <div class="pk-badge">★</div>
    </div>`;
  if (!window.L) return null;
  return window.L.divIcon({ html, className: '', iconSize: [54, 54], iconAnchor: [27, 27], popupAnchor: [0, -32] });
}

/* ─── Popup builder ────────────────────────────────────────────── */
function buildPopup(deal, userPos) {
  const c = CATS[deal.category] || CATS['Food & Drink'];
  const isPromo = deal.is_promoted;

  /* Distance */
  let distHtml = '';
  if (userPos && deal.lat && deal.lng) {
    const dist = haversineDistance(userPos.lat, userPos.lng, deal.lat, deal.lng);
    const info = formatDist(dist);
    distHtml = `<div style="display:flex;gap:10px;margin:6px 0;flex-wrap:wrap">
      <span style="font-size:11px;color:#8A9A8B">🚶 ${info.walk}</span>
      <span style="font-size:11px;color:#8A9A8B">🚗 ${info.drive}</span>
      <span style="font-size:11px;color:${c.color};font-weight:700">📍 ${info.label}</span>
    </div>`;
  }

  /* Time remaining */
  let timerHtml = '';
  if (deal.end_date) {
    const d = new Date(deal.end_date + 'T23:59:59');
    const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
    if (days > 0 && days <= 7) timerHtml = `<span style="font-size:10px;color:#FF7C7C;font-weight:700;background:#FF7C7C18;padding:2px 7px;border-radius:20px">⏰ ${days}d left</span>`;
    else if (days <= 0) timerHtml = `<span style="font-size:10px;color:#8A9A8B">Offer expired</span>`;
  }

  /* Claims */
  const claimsHtml = deal.claim_count > 0
    ? `<span style="font-size:10px;color:#8A9A8B">✓ ${deal.claim_count} claimed</span>` : '';

  /* Directions URL — works on all devices, no API key needed */
  const dirsUrl = (deal.lat && deal.lng)
    ? `https://www.google.com/maps/dir//${deal.lat},${deal.lng}`
    : `https://www.google.com/maps/search/${encodeURIComponent(deal.business_name + ' ' + (deal.city_name || ''))}`;

  /* iOS Apple Maps detection */
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const finalDirsUrl = isIOS && deal.lat && deal.lng
    ? `http://maps.apple.com/?daddr=${deal.lat},${deal.lng}&dirflg=w`
    : dirsUrl;

  /* Track marker click inline */
  const trackClick = `try{fetch('${API}/deals/${deal.deal_id}/track',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({event:'marker_click'})})}catch(e){}`;
  const trackDir   = `try{fetch('${API}/deals/${deal.deal_id}/track',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({event:'direction_request'})})}catch(e){}`;

  return `
    <div class="pk-popup">
      ${deal.image_url ? `<img class="pk-popup-img" src="${esc(deal.image_url)}" alt="" onerror="this.style.display='none'"/>` : ''}
      ${isPromo ? `<div class="pk-popup-promo-label">★ Promoted</div>` : ''}
      <div class="pk-popup-body">
        <p style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;color:#8A9A8B;margin:0 0 2px">${esc(deal.business_name)}</p>
        <h3 style="font-family:'Playfair Display',serif;font-size:14px;font-weight:700;color:#2B2B2B;margin:0 0 5px;line-height:1.3">${esc(deal.title)}</h3>
        <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-bottom:4px">
          ${deal.discount_text ? `<span style="font-size:11px;font-weight:700;color:${c.color};background:${c.color}22;padding:2px 8px;border-radius:20px">${esc(deal.discount_text)}</span>` : ''}
          ${deal.price === 0 ? `<span style="font-size:11px;font-weight:700;color:#5DBB63;background:#5DBB6320;padding:2px 8px;border-radius:20px">FREE</span>` : ''}
          ${timerHtml}
          ${claimsHtml}
        </div>
        ${distHtml}
        <div style="display:flex;gap:6px;margin-top:9px">
          <a href="/deals/${deal.deal_id}" onclick="${trackClick}"
             style="flex:1;text-align:center;padding:8px 4px;background:#2B2B2B;color:#F6F1E9;border-radius:20px;font-size:11px;font-weight:700;text-decoration:none">
            Get Claim Code ✦
          </a>
          <a href="${finalDirsUrl}" target="_blank" rel="noopener noreferrer" onclick="${trackDir}"
             title="Get Directions"
             style="display:flex;align-items:center;justify-content:center;width:36px;flex-shrink:0;background:#F6F1E9;border:1px solid rgba(138,154,139,.25);border-radius:20px;font-size:15px;text-decoration:none">
            🗺️
          </a>
        </div>
      </div>
    </div>`;
}

/* ─── Main component ───────────────────────────────────────────── */
export default function MapSection({ deals = [] }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markersRef   = useRef([]);
  const tileRef      = useRef(null);
  const userMarkerRef = useRef(null);

  const [loaded,       setLoaded]       = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [mapStyle,     setMapStyle]     = useState(0);
  const [locating,     setLocating]     = useState(false);
  const [userPos,      setUserPos]      = useState(null);

  /* Load Leaflet */
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const l = document.createElement('link');
      l.id = 'leaflet-css'; l.rel = 'stylesheet';
      l.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(l);
    }
    if (!document.getElementById('pk-css')) {
      const s = document.createElement('style');
      s.id = 'pk-css'; s.textContent = MARKER_CSS;
      document.head.appendChild(s);
    }
    if (window.L) { setLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLoaded(true);
    document.head.appendChild(script);
  }, []);

  /* Init map */
  useEffect(() => {
    if (!loaded || !containerRef.current || mapRef.current) return;
    const L = window.L;
    const map = L.map(containerRef.current, { zoomControl: false, attributionControl: true })
      .setView([33.9519, -83.3576], 15);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    const tile = L.tileLayer(MAP_STYLES[mapStyle].url, { attribution: '© CartoDB © OpenStreetMap', maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    tileRef.current = tile;
    return () => { map.remove(); mapRef.current = null; };
  }, [loaded]);

  /* Switch tile */
  useEffect(() => {
    if (!mapRef.current || !tileRef.current || !window.L) return;
    tileRef.current.remove();
    tileRef.current = window.L.tileLayer(MAP_STYLES[mapStyle].url, { attribution: '© CartoDB © OpenStreetMap', maxZoom: 19 }).addTo(mapRef.current);
  }, [mapStyle]);

  /* Sync markers — promoted deals first, larger icons */
  const syncMarkers = useCallback(() => {
    if (!mapRef.current || !window.L) return;
    markersRef.current.forEach(m => mapRef.current.removeLayer(m));
    markersRef.current = [];

    const base = activeFilter === 'All' ? deals : deals.filter(d => d.category === activeFilter);
    // Sort: promoted first
    const sorted = [...base].sort((a, b) => (b.is_promoted ? 1 : 0) - (a.is_promoted ? 1 : 0));

    sorted.forEach((deal, idx) => {
      if (!deal.lat || !deal.lng) return;
      const icon = deal.is_promoted
        ? buildPromoIcon(deal, idx * 60)
        : buildStdIcon(deal, idx * 60);
      if (!icon) return;

      const marker = window.L.marker([deal.lat, deal.lng], { icon, zIndexOffset: deal.is_promoted ? 1000 : 0 });
      marker.bindPopup(buildPopup(deal, userPos), { maxWidth: 300, minWidth: 240 });
      marker.on('popupopen', () => {
        // Track map view event
        fetch(`${API}/deals/${deal.deal_id}/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: 'map_view' }),
        }).catch(() => {});
      });
      marker.addTo(mapRef.current);
      markersRef.current.push(marker);
    });
  }, [deals, activeFilter, userPos]);

  useEffect(() => { syncMarkers(); }, [syncMarkers, loaded]);

  /* Locate user */
  const locateMe = () => {
    if (!mapRef.current || !window.L) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const newPos = { lat, lng };
        setUserPos(newPos);
        mapRef.current.flyTo([lat, lng], 16, { animate: true, duration: 1.2 });
        const L = window.L;

        // Remove old user marker
        if (userMarkerRef.current) { mapRef.current.removeLayer(userMarkerRef.current); }
        const pulse = L.divIcon({
          html: `<div style="width:16px;height:16px;border-radius:50%;background:#4F8EF7;border:3px solid white;box-shadow:0 0 0 8px rgba(79,142,247,.22)"></div>`,
          className: '', iconSize: [16, 16], iconAnchor: [8, 8],
        });
        const um = L.marker([lat, lng], { icon: pulse })
          .bindPopup('<div style="font-family:Montserrat,sans-serif;font-size:12px;font-weight:600;padding:4px 8px">📍 You are here</div>')
          .openPopup()
          .addTo(mapRef.current);
        userMarkerRef.current = um;
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  // Only promoted deals go on the map
  // If a promoted deal has no coords, use Athens city center as fallback
  const ATHENS_CENTER = [33.9519, -83.3576];
  const dealsWithCoords = deals.filter(d => d.is_promoted).map(d => ({
    ...d,
    lat: d.lat || ATHENS_CENTER[0],
    lng: d.lng || ATHENS_CENTER[1],
    _usedFallback: !d.lat || !d.lng,
  }));
  const dealsOnMap = dealsWithCoords;
  const filtered = activeFilter === 'All' ? dealsOnMap : dealsOnMap.filter(d => d.category === activeFilter);
  const nightMode  = mapStyle === 2;

  return (
    <div data-testid="map-section">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-1 ${nightMode ? 'text-[#C8A97E]' : 'text-[#8A9A8B]'}`}>
            Live Discovery Map
          </p>
          <h2 className={`text-2xl sm:text-3xl font-bold ${nightMode ? 'text-white' : 'text-[#2B2B2B]'}`}
              style={{ fontFamily: 'Playfair Display, serif' }}>
            Explore What&apos;s Happening Around You
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white rounded-xl border border-[#8A9A8B]/20 p-1" data-testid="map-style-toggle">
            {MAP_STYLES.map((s, i) => (
              <button key={s.id} onClick={() => setMapStyle(i)} data-testid={`map-style-${s.id}`}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${mapStyle === i ? 'bg-[#2B2B2B] text-white' : 'text-[#2B2B2B]/60 hover:text-[#2B2B2B]'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="text-xs text-[#2B2B2B]/50 hidden sm:block">
            <span className="text-[#C8A97E] font-bold">{filtered.length}</span> promoted deal{filtered.length !== 1 ? 's' : ''} on map
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap mb-4" data-testid="map-filter-pills">
        {FILTERS.map(f => {
          const c = CATS[f];
          const active = activeFilter === f;
          return (
            <button key={f} onClick={() => setActiveFilter(f)} data-testid={`map-filter-${f.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border-2 transition-all duration-200 ${active ? 'text-white border-transparent shadow-md' : 'bg-white border-[#8A9A8B]/20 text-[#2B2B2B] hover:border-[#C8A97E]/50'}`}
              style={active && c ? { background: c.color, boxShadow: `0 4px 12px ${c.glow}` } : active ? { background: '#2B2B2B' } : {}}>
              {c?.emoji} {f === 'All' ? 'All Deals' : f}
            </button>
          );
        })}
      </div>

      {/* Map */}
      <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ height: '520px' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%', background: '#f5f0e8' }} />

        {/* Status badge */}
        <div className="absolute top-4 left-4 z-[500] flex flex-col gap-2">
          <div className="bg-white/93 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-lg border border-[#8A9A8B]/10 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-bold text-[#2B2B2B]">Athens, GA</span>
            {filtered.length > 0 && (
              <span className="bg-[#C8A97E] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {filtered.length} promoted
              </span>
            )}
          </div>
        </div>

        {/* Locate Me */}
        <button onClick={locateMe} disabled={locating} title="Find deals near me"
          data-testid="locate-me-btn"
          className="absolute top-4 right-4 z-[500] w-10 h-10 bg-white rounded-xl shadow-lg border border-[#8A9A8B]/20 flex items-center justify-center text-[#2B2B2B] hover:border-[#C8A97E] transition-all disabled:opacity-60">
          {locating
            ? <div className="w-4 h-4 border-2 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
            : <Navigation size={16} />}
        </button>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[500] bg-white/93 backdrop-blur-sm rounded-2xl p-3 shadow-lg border border-[#8A9A8B]/10 hidden sm:block">
          <p className="text-xs font-bold text-[#2B2B2B]/50 mb-2 uppercase tracking-wider">Map Legend</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-[#C8A97E] flex items-center justify-center text-white text-xs font-bold">★</div>
              <span className="text-xs text-[#2B2B2B]/70 font-semibold">Promoted Deal</span>
            </div>
            {Object.entries(CATS).map(([cat, cfg]) => (
              <div key={cat} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs" style={{ background: cfg.color }}>{cfg.emoji}</div>
                <span className="text-xs text-[#2B2B2B]/65">{cat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Loading overlay */}
        {!loaded && (
          <div className="absolute inset-0 bg-[#F6F1E9] flex items-center justify-center z-[600]">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-[#2B2B2B]/50">Loading map...</p>
            </div>
          </div>
        )}
      </div>

      {/* Horizontal deal chips — promoted first */}
      {filtered.length > 0 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
          {[...filtered].sort((a, b) => (b.is_promoted ? 1 : 0) - (a.is_promoted ? 1 : 0)).map(deal => {
            const c = CATS[deal.category] || CATS['Food & Drink'];
            return (
              <Link key={deal.deal_id} to={`/deals/${deal.deal_id}`}
                data-testid={`map-deal-chip-${deal.deal_id}`}
                onClick={() => { if (mapRef.current && deal.lat && deal.lng) mapRef.current.flyTo([deal.lat, deal.lng], 17, { animate: true, duration: 0.8 }); }}
                className={`flex-shrink-0 flex items-center gap-2.5 bg-white rounded-2xl px-4 py-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${deal.is_promoted ? 'border-2 border-[#C8A97E]/50' : 'border border-[#8A9A8B]/12'}`}>
                {deal.logo_url && deal.is_promoted
                  ? <img src={deal.logo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-[#8A9A8B]/15" onError={e => { e.target.outerHTML = `<div style="width:32px;height:32px;border-radius:50%;background:${c.color};display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0">${c.emoji}</div>`; }} />
                  : <div className="w-8 h-8 rounded-full flex items-center justify-center text-base flex-shrink-0" style={{ background: c.color }}>{c.emoji}</div>
                }
                <div>
                  <div className="flex items-center gap-1">
                    {deal.is_promoted && <span className="text-xs text-[#C8A97E] font-bold">★</span>}
                    <p className="text-xs font-bold text-[#2B2B2B] line-clamp-1 max-w-[120px]">{deal.title}</p>
                  </div>
                  <p className="text-xs text-[#8A9A8B]">{deal.business_name}</p>
                </div>
                {deal.discount_text && <span className="text-xs font-bold ml-1 flex-shrink-0" style={{ color: c.color }}>{deal.discount_text}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
